import { useState, useEffect, useMemo } from 'react';

const CloudAlertSettingsTab = ({
    alertTTL: alertTTLProp,
    acceptRequired,
    finishRequired,
    reasonRequired,
    releaseTimeout,
    finishTimeout,
    clearTimeout: clearTimeoutProp,
    alertSettingId,
    onSave,
    saving,
}) => {
    const [ttl, setTtl] = useState(alertTTLProp || 60);
    const [accept, setAccept] = useState(!!acceptRequired);
    const [finish, setFinish] = useState(!!finishRequired);
    const [reason, setReason] = useState(!!reasonRequired);
    const [release, setRelease] = useState(releaseTimeout || 1);
    const [finishTime, setFinishTime] = useState(finishTimeout || 5);
    const [clearTime, setClearTime] = useState(clearTimeoutProp || 4);

    // Sync from props when data reloads (campus switch)
    useEffect(() => {
        setTtl(alertTTLProp || 60);
        setAccept(!!acceptRequired);
        setFinish(!!finishRequired);
        setReason(!!reasonRequired);
        setRelease(releaseTimeout || 1);
        setFinishTime(finishTimeout || 5);
        setClearTime(clearTimeoutProp || 4);
    }, [alertTTLProp, acceptRequired, finishRequired, reasonRequired, releaseTimeout, finishTimeout, clearTimeoutProp]);

    // Clamp slider values when TTL decreases
    useEffect(() => {
        if (release > ttl) setRelease(ttl);
        if (clearTime > ttl - 1) setClearTime(Math.max(4, ttl - 1));
        if (finishTime > ttl) setFinishTime(ttl);
    }, [ttl]);

    // Computed dirty detection — auto-resets when values match originals
    const isDirty = useMemo(() => {
        return (
            ttl !== (alertTTLProp || 60) ||
            accept !== !!acceptRequired ||
            finish !== !!finishRequired ||
            reason !== !!reasonRequired ||
            release !== (releaseTimeout || 1) ||
            finishTime !== (finishTimeout || 5) ||
            clearTime !== (clearTimeoutProp || 4)
        );
    }, [ttl, accept, finish, reason, release, finishTime, clearTime,
        alertTTLProp, acceptRequired, finishRequired, reasonRequired, releaseTimeout, finishTimeout, clearTimeoutProp]);

    // Cascading toggle logic
    const toggleAccept = () => {
        const next = !accept;
        setAccept(next);
        if (!next) {
            setFinish(false);
            setReason(false);
        }
    };

    const toggleFinish = () => {
        const next = !finish;
        setFinish(next);
        if (!next) setReason(false);
    };

    const toggleReason = () => {
        setReason(!reason);
    };

    // Range slider handlers
    const handleClearChange = (val) => {
        const v = parseInt(val);
        setClearTime(v);
        if (v >= finishTime) setFinishTime(v + 1);
    };

    const handleFinishChange = (val) => {
        const v = parseInt(val);
        if (v > clearTime) {
            setFinishTime(v);
        }
    };

    const handleTtlChange = (e) => {
        let v = parseInt(e.target.value);
        if (isNaN(v)) v = 60;
        v = Math.max(60, Math.min(360, v));
        // Round to nearest 5
        v = Math.round(v / 5) * 5;
        setTtl(v);
    };

    const handleSave = () => {
        onSave({
            alertSettingId,
            acceptRequired: accept,
            finishRequired: finish,
            reasonRequired: reason,
            releaseTimeout: release,
            finishTimeout: finishTime,
            clearTimeout: clearTime,
            alertTTL: ttl,
        });
    };

    const handleCancel = () => {
        setTtl(alertTTLProp || 60);
        setAccept(!!acceptRequired);
        setFinish(!!finishRequired);
        setReason(!!reasonRequired);
        setRelease(releaseTimeout || 1);
        setFinishTime(finishTimeout || 5);
        setClearTime(clearTimeoutProp || 4);
    };

    // Dynamic slider ranges based on TTL
    const releaseMax = ttl;
    const clearMax = ttl - 1;
    const finishMax = ttl;

    // Slider position helpers
    const releasePercent = releaseMax > 1 ? ((release - 1) / (releaseMax - 1)) * 100 : 0;
    const finishPercent = finishMax > 5 ? ((finishTime - 5) / (finishMax - 5)) * 100 : 0;
    const clearPercent = clearMax > 4 ? ((clearTime - 4) / (clearMax - 4)) * 100 : 0;

    const toggleRows = [
        {
            key: 'accept',
            title: 'Accept Alerts',
            desc: 'Users must accept alerts being responded to.',
            value: accept,
            onChange: toggleAccept,
            disabled: false,
        },
        {
            key: 'finish',
            title: 'Finish Alerts',
            desc: 'Users must mark when alerts are complete.',
            value: finish,
            onChange: toggleFinish,
            disabled: !accept,
        },
        {
            key: 'reason',
            title: 'Record Reason',
            desc: 'Users can record a reason for the alert.',
            value: reason,
            onChange: toggleReason,
            disabled: !accept || !finish,
        },
    ];

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <div>
                    <h2 className="pp-section__title">Alert Behavior Settings</h2>
                    <p className="ss-section-desc">
                        Configure alert timeouts, interaction requirements, and the maximum alert lifetime.
                    </p>
                </div>
                {isDirty ? (
                    <div className="pp-section__actions">
                        <button className="dp-btn dp-btn--cancel" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="pp-section__body">
                {/* Max Alert Timeout (TTL) */}
                <div className="ss-field" style={{ maxWidth: '200px', marginBottom: 'var(--space-4)' }}>
                    <label className="ss-field__label">Max Alert Timeout (minutes)</label>
                    <input
                        className="ss-number-input"
                        type="number"
                        min={60}
                        max={360}
                        step={5}
                        value={ttl}
                        onChange={handleTtlChange}
                        onBlur={handleTtlChange}
                    />
                    <p className="ss-field__hint">Range: 60 – 360 minutes</p>
                </div>

                {/* Toggle rows with cascading */}
                {toggleRows.map((t) => (
                    <div key={t.key} className={`ss-toggle-row${t.disabled ? ' is-disabled' : ''}`}>
                        <div className="ss-toggle-row__info">
                            <h4 className="ss-toggle-row__title">{t.title}</h4>
                            <p className="ss-toggle-row__desc">{t.desc}</p>
                        </div>
                        <div className="ss-toggle-row__control">
                            <div
                                className={`ss-toggle__track${t.value ? ' is-active' : ''}`}
                                onClick={t.disabled ? undefined : t.onChange}
                                role="switch"
                                aria-checked={t.value}
                                tabIndex={t.disabled ? -1 : 0}
                                onKeyDown={(e) => {
                                    if (!t.disabled && (e.key === 'Enter' || e.key === ' ')) {
                                        e.preventDefault();
                                        t.onChange();
                                    }
                                }}
                            >
                                <div className="ss-toggle__knob" />
                            </div>
                        </div>
                    </div>
                ))}

                {/* Release Timeout slider */}
                <div className="ss-range">
                    <div className="ss-range__header">
                        <h4 className="ss-range__title">Release Timeout</h4>
                    </div>
                    <p className="ss-range__desc">
                        Time to release alert after accepted with no device clear.
                    </p>
                    <div className="ss-range__track-wrap">
                        <span
                            className="ss-range__value"
                            style={{ left: `calc(${releasePercent}% + ${10 - releasePercent * 0.175}px)` }}
                        >
                            {release} Min
                        </span>
                        <input
                            className="ss-range__input"
                            type="range"
                            min={1}
                            max={releaseMax}
                            step={1}
                            value={release}
                            onChange={(e) => setRelease(parseInt(e.target.value))}
                        />
                    </div>
                    <div className="ss-range__labels"><span>1 Min</span><span>{releaseMax} Min</span></div>
                </div>

                {/* Finish Timeout slider */}
                <div className="ss-range">
                    <div className="ss-range__header">
                        <h4 className="ss-range__title">Finish Timeout</h4>
                    </div>
                    <p className="ss-range__desc">
                        Time to auto-finish alarm if stale after accept and clear.
                    </p>
                    <div className="ss-range__track-wrap">
                        <span
                            className="ss-range__value"
                            style={{ left: `calc(${finishPercent}% + ${10 - finishPercent * 0.175}px)` }}
                        >
                            {finishTime} Min
                        </span>
                        <input
                            className="ss-range__input"
                            type="range"
                            min={5}
                            max={finishMax}
                            step={1}
                            value={finishTime}
                            onChange={(e) => handleFinishChange(e.target.value)}
                        />
                    </div>
                    <div className="ss-range__labels"><span>5 Min</span><span>{finishMax} Min</span></div>
                </div>

                {/* Clear Timeout slider */}
                <div className="ss-range">
                    <div className="ss-range__header">
                        <h4 className="ss-range__title">Clear Timeout</h4>
                    </div>
                    <p className="ss-range__desc">
                        Time to auto-clear alert after no contact from system.
                    </p>
                    <div className="ss-range__track-wrap">
                        <span
                            className="ss-range__value"
                            style={{ left: `calc(${clearPercent}% + ${10 - clearPercent * 0.175}px)` }}
                        >
                            {clearTime} Min
                        </span>
                        <input
                            className="ss-range__input"
                            type="range"
                            min={4}
                            max={clearMax}
                            step={1}
                            value={clearTime}
                            onChange={(e) => handleClearChange(e.target.value)}
                        />
                    </div>
                    <div className="ss-range__labels"><span>4 Min</span><span>{clearMax} Min</span></div>
                </div>
            </div>
        </div>
    );
};

export default CloudAlertSettingsTab;
